/**
 * Browser-safe exports from @repo/core
 *
 * This file exports only modules that are safe to use in client components.
 * These modules do NOT import from @repo/database or other server-only packages.
 *
 * Import from "@repo/core/client" instead of "@repo/core" in client components.
 */

// Page intent classification (types and utilities)
export * from "../utils/page-intent-classifier";

// Property schemas (Zod schemas for form validation)
export * from "../schemas/property";

// Subject-type vocabulary + the "can this subject parent LOCATION children?"
// predicate. Client-safe (zod only) and named explicitly rather than star-
// exported so the server-oriented create/update schemas in ../types/property
// stay out of the client bundle.
export {
	subjectTypeValues,
	subjectTypeSchema,
	canParentLocations,
	type SubjectType,
} from "../types/property";

// Discovery query types (schemas and type definitions)
export * from "../types/discovery-query";

// Recommendation categories: category / reason vocabularies, UI labels, the
// filter and response types the dashboard renders, plus the pure brandKey and
// phrase normalizers. Types and pure functions only.
export * from "../types/recommendation-categories";
export * from "../utils/brand-key";
export * from "../utils/phrase-normalize";

// Personas: the field vocabularies, the deterministic first-person context
// renderer (what we send), the one-line prompt join, the context hash, the
// role-first display labels, and the zod contract + DTO types the API, the
// product and the free prompt generator share. Pure; zod only.
export * from "../utils/persona-context";
export * from "../types/personas";
export * from "../types/persona-lens";
export * from "../utils/rate-interval";

// Free prompt generator: the claim path, the "Track these personas" signup URL
// and the shareable result URL, plus the framework grouping of a
// generation's stored results. Pure helpers, used by the marketing UI and the
// results email alike.
export * from "../utils/prompt-generator-claim";
export * from "../utils/prompt-generator-results";
// The generation import ("Track these personas") request schemas and wire types.
export * from "../types/prompt-generator-import";

// What a coverage group is scoped to — one property's coverage, or an
// organization's cross-brand list. Type-only: the web layer keys its query
// keys and route params on the discriminated union, and the guards that go
// with it stay on the server barrel where the services use them.
export type { CoverageScope } from "../types/coverage-scope";

// SERP rank bands — the ordinal vocabulary the SEO-opportunities service
// computes movement in and the drill-down renders as chips. Pure constants.
export * from "../types/rank-bands";

// Grounding-search rank tracking pause: the suspect-data cutoff and the
// window-end helper the SEO dashboard anchors its date windows on, plus the
// no-rank (v2) report shape and its type guard.
export * from "../services/grounding-search-list";
export * from "../services/grounding-serp-tracking";

// Prompt-class detection (pure text heuristics — brand/comparison suggestions)
export * from "../utils/prompt-class-detection";

// Prompt coverage + similarity types (client-safe, no runtime deps)
export * from "../types/prompt-coverage";

// PQS score-tier boundaries — shared by the placements API (tier filtering)
// and the web UI (tier badges). Pure constants + functions.
export * from "./placement-tiers";

// Add-on prompt platforms (Claude, Perplexity) — the allowlist and labels the
// admin UI, the API validator, and the nightly dispatch all share.
export * from "../config/add-on-platforms";
// Claude tracking: the pinned model, request guardrails, stored
// vocabularies and list prices (the admin pool control shows them). Pure.
export * from "../config/claude-tracking";

// Organization trial tools (YouTube Optimizer, ...) — the allowlist and labels
// the admin Tools dialog, the plan endpoint and the API gate share.
export * from "../config/organization-tools";

// YouTube Optimizer: statuses, limits, request schemas, the description
// renderer, captions, corrections, hand edits and the publish match — pure
// functions and types only. The YouTube URL parser is the same one the
// server uses.
export * from "../services/youtube-optimizer/client";
export { extractYouTubeVideoId } from "../utils/youtube-url";
// The API's run DTOs, as types only (erased at build, so nothing server-side
// reaches the browser bundle).
export type {
	VideoOptimizationDetail,
	VideoOptimizationSummary,
	VideoOptimizerUsage,
} from "../services/youtube-optimizer/service";
// The customer Claude pool screens' API shapes, as types only.
export type {
	ClaudeCustomerChange,
	ClaudeCustomerOverview,
	ClaudeCustomerProject,
} from "../services/claude-pool-customer";
export type {
	ClaudeCustomerImpact,
	ClaudePoolRefusal,
} from "../services/claude-project-setting";

// Prompt-coverage scoring utilities (pure — constants, funnel mapping,
// composite score math, client-side selection simulation + greedy
// maximization). Safe to import into client components.
export * from "../utils/prompt-coverage-scoring";

// Funnel-stage breakdown types + reader helpers — shared between the
// historical-metrics writer/recompute (server) and the dashboard funnel hero
// + drill-down (client). Pure types and pure functions, no DB deps.
export * from "../utils/funnel-breakdown";

// The headline Share of Voice definition (consideration + decision, pooled
// window) + stage-scoped competitor pooling — shared by the brand dashboard
// hero (client) and the sov-summary / competitor-sov endpoints (server) so
// every surface labeled "Share of Voice" shows the same number.
export * from "../utils/headline-sov";

// Tracked-brand ("competitor") name matching — the one matcher the metric
// writer and the answer drill-down share, so a list of brands under a raw
// answer reconciles against the Brands page's counts. Pure, no DB deps.
export * from "../utils/competitor-match";

// The canonical brand-mention matcher (normalization + whole-word run match +
// one-mention-one-entity resolution) and the pure competitor share-of-voice
// computation built on it. The web report's logo grid and SOV table use these
// so they cannot disagree with the export or the stored analysis.
export * from "../utils/brand-mention-match";
export * from "../utils/competitor-sov-compute";

// Discovery utilities (brand matching helpers and share of voice calculation)
export {
	isOurBrand,
	ourBrandMatchFocal,
	isBrandMentionedInExecution,
	isCompetitorBrand,
	isCompetitorMentionedInExecution,
	getCompetitorNames,
	transformRawGoogleAiOverview,
	calculateQueryShareOfVoice,
	calculateShareOfVoice,
	calculateSummaryStatistics,
} from "../utils/discovery";

// AI Overview brand-mention detection — shared between storage paths and the
// report viewer's "Brand in AI Overview" badge. Single source of truth so
// the badge, tab dot, mention count, and CompetitorSOV always agree.
export {
	detectOurBrandInAiOverview,
	detectCompetitorsInAiOverview,
	detectOurBrandInProseText,
	detectCompetitorsInProseText,
	type AiOverviewForDetection,
	type AiOverviewReferenceLike,
	type AiOverviewTextMentionLike,
	type AiOverviewSectionLike,
	type BrandIdentityForDetection,
	type DetectOurBrandInAiOverviewResult,
	type AiOverviewDetectionTier,
} from "../utils/ai-overview-detection";

// Recommendations utilities (platform consistency analysis)
export {
	analyzePlatformConsistency,
	getPlatformGuideUrl,
} from "../utils/recommendations";

// Markdown rendering utilities
export {
	renderMarkdownToHtml,
	detectMarkdown,
	highlightBrandsInHtml,
	preprocessMixedContent,
	cleanAiOverviewMarkdown,
	cleanAiOverviewMarkdownWithLists,
	getAiOverviewFullMarkdown,
} from "../utils/markdown-renderer";

// Platform display constants and utilities
export {
	PLATFORM_DISPLAY_NAMES,
	PLATFORM_LOGOS,
	PLATFORM_ORDER,
	getPlatformDisplayName,
	getPlatformLogo,
	getGroundingPlatformLogo,
	getGroundingPlatformDisplayName,
	getPlatformOrder,
	sortPlatformsByOrder,
	sortPlatformKeysByOrder,
} from "../utils/platform";

// Domain utilities (www stripping, URL normalization, guarded own-domain matching)
export {
	stripWwwFromDomain,
	stripWwwFromUrl,
	normalizeDomainOrNull,
	isUrlOnDomain,
	isOwnDomainCitation,
	isPeerUrl,
	propertyFocalDomain,
	type PropertyFocalDomainSource,
} from "../utils/domain";

// The public free tools' domain parser. Pure (parse-domain only), so the
// browser form rejects exactly what the API handler and the per-domain route
// reject, and every surface normalizes to the same canonical hostname.
export {
	parsePublicDomain,
	resolveDomainSegment,
	type ParsePublicDomainResult,
	type PublicDomainFailure,
	type DomainSegmentResolution,
} from "../utils/public-domain";

// `site:`-operator parsing. Pure (regex + domain normalization), so the fan-out
// lists and pickers can hide/label scoped searches with exactly the predicate the
// server-side SERP and recommendation exclusions branch on.
export {
	parseSiteScope,
	isSiteScopedQuery,
	buildSiteScopeClassifier,
	isBrandAffineDomain,
	brandAffinityStrength,
	competitorLookalikeStrength,
	isCompetitorLookalike,
	isFirstPartyLookalike,
	SITE_SCOPED_QUERY_MESSAGE,
	type SiteScopeParseResult,
	type SiteScopeClass,
	type SiteScopeClassification,
	type SiteScopeClassifier,
	type SiteScopeClassifierContext,
	type SiteScopeCompetitorSource,
	type BrandAffinityContext,
	type LookalikeCompetitorSource,
	type FirstPartyLookalikeContext,
} from "../utils/site-scope";

// Competitor-NAMED (non-`site:`-scoped) fan-out matching. Pure like the block
// above, and the same exclusion predicate the SERP and recommendation paths
// branch on: a query that names a competitor and NOT us.
export {
	buildCompetitorNamedQueryMatcher,
	type CompetitorNamedQueryContext,
	type CompetitorNamedQueryMatch,
	type CompetitorNamedQueryMatcher,
} from "../utils/competitor-named-query";

// Property display naming — always prefer this over reading `.domain`, which is
// null for PERSON/CATEGORY subjects and renders as "null" in template strings.
export {
	getPropertyLabel,
	getPropertySlug,
	type PropertyLabelSource,
} from "../utils/property-label";

// Publisher kind derivation + platform helpers (pure, no DB runtime deps —
// only type-only imports from @repo/database which are erased at compile time)
export {
	AI_POLICY_BLOCKED_BOT_SELECT,
	type AiAccessCheckResult,
	type AiIndexerFlag,
	type AiPolicyReasons,
	buildAiIndexerFlags,
	deriveAiPolicyReasons,
	derivePublisherAiPolicyDetail,
	derivePublisherKind,
	extractPlatformDomain,
	extractPublisherDomain,
	isPlatformDomain,
	PLATFORM_DOMAINS,
	PUBLISHER_BLOCKED_BOT_CATEGORIES,
	type PublisherAiPolicyDetail,
	type PublisherBlockedBotCategory,
	type RobotsTxtState,
	resolveAiPlatformLabel,
} from "../types/publisher";

// Authority types that never take a pitch (government / academic /
// encyclopedia) + the "any match" predicate. Pure constants and functions —
// the only @repo/database import in that module is type-only, so it is erased
// before the client bundle sees it. Shared so the L2 selection surfaces, the
// server rollups and the export gap recommendations exclude the same set.
export {
	NON_PITCHABLE_AUTHORITY_TYPES,
	isNonPitchableAuthority,
	hasNonPitchableAuthority,
} from "../types/citation-intelligence";

// Report progress tracking (types and utilities for progress display)
export * from "../types/report-progress";

// Site audit finding labels (human-readable titles and descriptions)
export {
	resolveFindingLabel,
	type FindingLabel,
} from "../utils/audit-finding-labels";

// The seven audited dimensions + the issueKey → dimension mapping. Pure
// constants shared by the audit service, the dashboard card, and the L2.
export * from "../types/site-audit-dimensions";

// Brandfetch Logo CDN URL builder (client-safe, no server dependencies)
export { getBrandfetchLogoUrl } from "../utils/brandfetch-logo";

// Chunk boundary marker utilities (shared between web client and export)
export {
	insertBoundaryMarkers,
	adjustForStructuralPrefix,
	EMOJI_MARKERS,
	type MarkerSet,
	type StructuralDesert,
	type WindowAlignment,
	type BestChunk,
} from "../utils/chunk-boundary-markers";

// Onboarding sequence logic (pure step calculation, no DB dependency)
export {
	getOnboardingSteps,
	type OnboardingPersona,
	type OnboardingPhase,
	type OnboardingStep,
	type PropertyOnboardingState,
	type OnboardingSequenceResult,
} from "../services/onboarding-steps";

// Which setup email a person gets next (pure planner, no DB dependency)
export {
	advanceSequenceState,
	isSequenceEligible,
	planSetupEmail,
	resolveSequenceState,
	type OnboardingSequenceState,
	type PlanSetupEmailInput,
	type SetupEmailPlan,
	type SetupEmailVariant,
	type SetupSequenceOrganization,
	type SetupSequenceProperty,
	type SetupSequenceUser,
} from "../services/setup-sequence";

// Period-over-period windowing for daily metric series (brand dashboard L1)
export {
	aggregateWindowDelta,
	collapseDaily,
	type AggregateWindowOptions,
	type WindowDelta,
	type WindowMode,
} from "../utils/window-aggregation";

// Agency white-label client portal — the pure half.
//
// Wire types plus the slug/hostname/password rules the settings form validates
// against, so the browser rejects exactly what the server would. Note the
// password *rules* come from ./client-portal/password-rules rather than
// ../services/client-portal/password: that module pulls in better-auth/crypto
// (jose + noble ciphers), which has no business in a client bundle.
export * from "../services/client-portal/types";
export {
	PORTAL_SLUG_RE,
	PORTAL_RESERVED_SLUGS,
	normalizePortalSlug,
	validatePortalSlug,
	defaultPortalSlug,
	type PortalSlugValidation,
} from "../services/client-portal/slug";
export {
	normalizePortalHostname,
	validatePortalHostname,
	type PortalHostnameValidation,
} from "../services/client-portal/hostname";
export {
	PORTAL_PASSWORD_MIN,
	PORTAL_PASSWORD_MAX,
	validatePortalPassword,
	type PortalPasswordValidation,
} from "../services/client-portal/password-rules";
export { VERCEL_CNAME_TARGET } from "../services/client-portal/domain-provider";

// Mode-aware UI configuration derived from a property's `subjectType`. Pure
// (a type-only import of SubjectType). Moved out of
// apps/web/modules/saas/properties/lib so the workflow catalog can gate
// playbooks on exactly the flags the UI gates sections on; that file is now a
// re-export of this one.
export {
	subjectModeConfig,
	type SubjectModeConfig,
	type PropertyProjectType,
} from "../utils/subject-mode";

// Workflow engine — types, observed-state shapes, the pure evaluator, and the
// catalog. The state loader is deliberately absent: it reads @repo/database.
export * from "../workflows/client";

// Organization data export: the data-set catalog the request form renders, the
// request-scope schema the form and the API route share, and the delivered
// file shape. Zod only; the builders that read the database stay server-side.
export * from "../services/data-export/catalog";

// Per-property notifications: email category vocabulary + launch window,
// property settings tab allowlist / deep-link builder, and the wrong-domain
// investigation types + rule-based diagnosis. Pure; type-only database import.
export * from "../utils/email-categories";
export * from "../utils/settings-tabs";
export * from "../utils/wrong-domain-diagnosis";
